// Markdown builder for the workout history export. Pure function over
// the already-fetched sessions array — no DB access, no formatting
// dependencies. The caller hands in `i18n.language` so the headings
// pick the right Arabic vs English labels without dragging useTranslation
// into a non-React module.

const LABELS = {
  ar: {
    title: 'سجل التمارين',
    exportedOn: 'تاريخ التصدير',
    totalSessions: 'إجمالي الجلسات',
    dateRange: 'نطاق التواريخ',
    exercises: 'تمارين',
    workSets: 'سيتات (بدون إحماء)',
    totalVolume: 'إجمالي الحمل',
    cardio: 'كارديو',
    bodyweight: 'الوزن',
    energy: 'الطاقة',
    notes: 'ملاحظات',
    sessionType: 'النوع',
    duration: 'المدة',
    minutes: 'دقيقة',
    set: 'سيت',
    warmup: 'إحماء',
    failure: 'فشل',
    rpe: 'RPE',
    cardioBlock: 'كارديو',
    calories: 'سعرات',
    metric: 'القيمة',
    value: 'الرقم',
    noWeight: '(جسم)',
  },
  en: {
    title: 'Workout History',
    exportedOn: 'Exported on',
    totalSessions: 'Total sessions',
    dateRange: 'Date range',
    exercises: 'Exercises',
    workSets: 'Work sets',
    totalVolume: 'Total volume',
    cardio: 'Cardio',
    bodyweight: 'Bodyweight',
    energy: 'Energy',
    notes: 'Notes',
    sessionType: 'Type',
    duration: 'Duration',
    minutes: 'min',
    set: 'Set',
    warmup: 'warmup',
    failure: 'failure',
    rpe: 'RPE',
    cardioBlock: 'Cardio',
    calories: 'kcal',
    metric: 'Metric',
    value: 'Value',
    noWeight: '(bodyweight)',
  },
}

function fmtNumber(n, decimals = 0) {
  if (n == null || isNaN(n)) return '—'
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function sessionTotals(session) {
  const logs = session.exercise_logs || []
  const cardios = session.cardio_logs || []
  let workSets = 0
  let totalVolume = 0
  for (const l of logs) {
    for (const set of l.sets || []) {
      if (set.is_warmup) continue
      workSets += 1
      const w = Number(set.weight_kg) || 0
      const r = Number(set.reps) || 0
      totalVolume += w * r
    }
  }
  const cardioMin = cardios.reduce((s, c) => s + (Number(c.duration_min) || 0), 0)
  return {
    exerciseCount: logs.length,
    workSets,
    totalVolume,
    cardioMin,
  }
}

function buildSessionMarkdown(session, L) {
  const totals = sessionTotals(session)
  const out = []
  const title = session.session_type
    ? session.session_type
    : `${L.title} — ${session.date || ''}`
  out.push(`## ${title}`)
  out.push(`**${session.date || ''}**`)
  out.push('')

  out.push(`| ${L.metric} | ${L.value} |`)
  out.push('|---|---|')
  out.push(`| ${L.exercises} | ${totals.exerciseCount} |`)
  out.push(`| ${L.workSets} | ${totals.workSets} |`)
  out.push(`| ${L.totalVolume} | ${fmtNumber(totals.totalVolume)} kg |`)
  if (totals.cardioMin > 0) {
    out.push(`| ${L.cardio} | ${fmtNumber(totals.cardioMin)} ${L.minutes} |`)
  }
  if (session.duration_min) {
    out.push(`| ${L.duration} | ${fmtNumber(session.duration_min)} ${L.minutes} |`)
  }
  if (session.body_weight_kg) {
    out.push(`| ${L.bodyweight} | ${fmtNumber(session.body_weight_kg, 1)} kg |`)
  }
  if (session.energy_rating != null) {
    out.push(`| ${L.energy} | ${session.energy_rating}/5 |`)
  }
  out.push('')

  const logs = [...(session.exercise_logs || [])].sort(
    (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
  )
  if (logs.length > 0) {
    out.push(`### ${L.exercises}`)
    out.push('')
    logs.forEach((log, idx) => {
      const exName = log.exercise?.name || `#${log.exercise_id || '?'}`
      const group = log.exercise?.muscle_group ? ` _(${log.exercise.muscle_group})_` : ''
      out.push(`#### ${idx + 1}. ${exName}${group}`)
      const sets = [...(log.sets || [])].sort(
        (a, b) => (a.set_number ?? 0) - (b.set_number ?? 0),
      )
      for (const set of sets) {
        const w = set.weight_kg != null && set.weight_kg !== 0
          ? `${fmtNumber(set.weight_kg, set.weight_kg % 1 ? 1 : 0)} kg`
          : L.noWeight
        const r = set.reps != null ? `× ${set.reps}` : ''
        const tags = []
        if (set.is_warmup) tags.push(L.warmup)
        if (set.is_failure) tags.push(L.failure)
        if (set.rpe != null) tags.push(`${L.rpe} ${set.rpe}`)
        const suffix = tags.length ? ` _(${tags.join(', ')})_` : ''
        out.push(`- ${L.set} ${set.set_number ?? '?'}: **${w} ${r}**${suffix}`)
      }
      if (log.notes) {
        out.push('')
        out.push(`> ${log.notes}`)
      }
      out.push('')
    })
  }

  const cardios = session.cardio_logs || []
  if (cardios.length > 0) {
    out.push(`### ${L.cardio}`)
    out.push('')
    for (const c of cardios) {
      const parts = []
      if (c.cardio_type) parts.push(c.cardio_type)
      if (c.duration_min) parts.push(`${fmtNumber(c.duration_min)} ${L.minutes}`)
      if (c.calories_estimated) parts.push(`~${fmtNumber(c.calories_estimated)} ${L.calories}`)
      out.push(`- ${parts.join(' · ') || L.cardioBlock}`)
    }
    out.push('')
  }

  if (session.notes) {
    out.push(`### ${L.notes}`)
    out.push('')
    out.push(`> ${session.notes}`)
    out.push('')
  }

  out.push('---')
  out.push('')
  return out.join('\n')
}

export function buildWorkoutMarkdown(sessions, language = 'en') {
  const L = LABELS[language === 'ar' ? 'ar' : 'en']
  const today = new Date().toLocaleDateString('en-CA')
  const dated = sessions.filter((s) => s.date).map((s) => s.date).sort()
  const range = dated.length > 0
    ? (dated[0] === dated[dated.length - 1]
        ? dated[0]
        : `${dated[0]} → ${dated[dated.length - 1]}`)
    : '—'

  const header = [
    `# ${L.title}`,
    '',
    `**${L.exportedOn}**: ${today}  `,
    `**${L.totalSessions}**: ${sessions.length}  `,
    `**${L.dateRange}**: ${range}`,
    '',
    '---',
    '',
  ].join('\n')

  const body = sessions.map((s) => buildSessionMarkdown(s, L)).join('\n')
  return header + body
}

// Helper for the file name — yyyy-mm-dd-style stamp so multiple
// exports sort chronologically.
export function suggestedFilename(language = 'en') {
  const stamp = new Date().toISOString().slice(0, 10)
  const base = language === 'ar' ? 'سجل-التمارين' : 'workout-history'
  return `${base}-${stamp}.md`
}
